// NOTE: This file is stable and usually should not be modified.
// It is important that all functionality in this file is preserved, and should only be modified if explicitly requested.

import { ChevronDown, LogOut, UserIcon, UserPlus, Heart, User, Moon, Sun, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Capacitor } from '@capacitor/core';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx';
import { useLoggedInAccounts, type Account } from '@/hooks/useLoggedInAccounts';
import { useTheme } from '@/hooks/useTheme';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { genUserName } from '@/lib/genUserName';
import { safeImageUrl } from '@/lib/utils';

interface AccountSwitcherProps {
  onAddAccountClick: () => void;
}

export function AccountSwitcher({ onAddAccountClick }: AccountSwitcherProps) {
  const { currentUser, otherUsers, setLogin, removeLogin } = useLoggedInAccounts();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { installable, installing, installed, install } = usePWAInstall();

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const handleInstall = async () => {
    await install();
  };

  // Don't show install option on native platforms (APK/iOS app)
  const isNative = Capacitor.isNativePlatform();
  // Show install option if app is installable OR not yet installed (link to instructions)
  const showInstallOption = !isNative && (installable || !installed);

  if (!currentUser) return null;

  const getDisplayName = (account: Account): string => {
    return account.metadata.name ?? genUserName(account.pubkey);
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button className='flex items-center gap-1 p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-all text-foreground'>
          <Avatar className='w-9 h-9'>
            <AvatarImage src={safeImageUrl(currentUser.metadata.picture)} alt={getDisplayName(currentUser)} />
            <AvatarFallback>{getDisplayName(currentUser).charAt(0)}</AvatarFallback>
          </Avatar>
          <ChevronDown className='w-4 h-4 text-muted-foreground' />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-56 p-2 animate-scale-in z-[100]'>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            toggleTheme();
          }}
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          {theme === 'dark' ? (
            <Sun className='w-4 h-4 text-amber-400' />
          ) : (
            <Moon className='w-4 h-4 text-violet-500' />
          )}
          <span>{theme === 'dark' ? 'light mode' : 'dark mode'}</span>
        </DropdownMenuItem>
        {showInstallOption && (
          <>
            {installable ? (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleInstall();
                }}
                className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
                disabled={installing}
              >
                <Download className='w-4 h-4 text-blue-500' />
                <span>{installing ? 'installing...' : 'install app'}</span>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem asChild>
                <Link
                  to='/install'
                  className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
                >
                  <Download className='w-4 h-4 text-blue-500' />
                  <span>install app</span>
                </Link>
              </DropdownMenuItem>
            )}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate(`/${nip19.npubEncode(currentUser.pubkey)}`)}
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          <User className='w-4 h-4' />
          <span>my profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/cherished')}
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          <Heart className='w-4 h-4 text-violet-500' />
          <span>cherished hues</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {otherUsers.length > 0 && (
          <>
            <div className='font-medium text-sm px-2 py-1.5'>Switch Account</div>
            {otherUsers.map((user) => (
              <DropdownMenuItem
                key={user.id}
                onClick={() => setLogin(user.id)}
                className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
              >
                <Avatar className='w-8 h-8'>
                  <AvatarImage src={safeImageUrl(user.metadata.picture)} alt={getDisplayName(user)} />
                  <AvatarFallback>{getDisplayName(user)?.charAt(0) || <UserIcon />}</AvatarFallback>
                </Avatar>
                <div className='flex-1 truncate'>
                  <p className='text-sm font-medium'>{getDisplayName(user)}</p>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuItem
          onClick={onAddAccountClick}
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          <UserPlus className='w-4 h-4' />
          <span>Add another account</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => removeLogin(currentUser.id)}
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md text-red-500'
        >
          <LogOut className='w-4 h-4' />
          <span>Log out</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className='flex items-center gap-2 cursor-pointer p-2 rounded-md text-muted-foreground'
          >
            <span>🎭</span>
            <span>vibed with shakespeare</span>
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}